import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { companyAPI, achievementAPI } from '../services/api';
import Layout from '../components/Layout';
import { DEFAULT_APP_LOGO } from '../constants';
import WorkingHoursManager from '../components/WorkingHoursManager';
import ServiceTemplatesManager from '../components/ServiceTemplatesManager';
import TechniciansManager from '../components/TechniciansManager';
import UserConnectionsManager from '../components/UserConnectionsManager';
import MarketingCampaignManager from '../components/MarketingCampaignManager';
import CompanyReviewsManager from '../components/CompanyReviewsManager';
import SquareIntegrationSettings from '../components/SquareIntegrationSettings';
import EnterpriseLockedOverlay from '../components/EnterpriseLockedOverlay';
import Icon from '../components/Icon';

const ENTERPRISE_LOCKED_FEATURES = {
  'booking-settings': { title: 'Booking', description: 'Online booking calendar and appointment management with buffer times and availability controls.' },
  'service-templates': { title: 'Service Templates', description: 'Define service types, durations and default prices for your workshops.' },
  'technicians': { title: 'Technicians', description: 'Manage technician accounts, roles and time off.' },
  'marketing': { title: 'Marketing', description: 'Email campaigns and promotional outreach to your customers.' },
  'integrations': { title: 'Integrations', description: 'Connect Square for service tickets, payments, catalog sync, and booking imports.' },
};


// Achievement tier colors
const TIER_COLORS = {
  bronze: { bg: 'rgba(205, 127, 50, 0.15)', border: '#cd7f32', text: '#cd7f32' },
  silver: { bg: 'rgba(192, 192, 192, 0.15)', border: '#c0c0c0', text: '#c0c0c0' },
  gold: { bg: 'rgba(255, 215, 0, 0.15)', border: '#ffd700', text: '#ffd700' },
  platinum: { bg: 'rgba(229, 228, 226, 0.15)', border: '#e5e4e2', text: '#e5e4e2' },
};

const getXpMilestones = (templates = []) => (
  (templates || [])
    .filter((template) => template?.category === 'xp' && Number(template.threshold || 0) > 0)
    .sort((a, b) => Number(a.threshold || 0) - Number(b.threshold || 0))
);

const getXpTrackInfo = (totalXP, templates) => {
  const milestones = getXpMilestones(templates);
  const baseMilestone = { id: 'xp_start', name: 'Start', threshold: 0 };
  if (!milestones.length) {
    return {
      milestones: [],
      currentMilestone: baseMilestone,
      nextMilestone: null,
      levelProgress: 0,
      remainingXP: 0,
    };
  }

  const currentMilestone = milestones
    .filter((milestone) => totalXP >= Number(milestone.threshold || 0))
    .pop() || baseMilestone;
  const nextMilestone = milestones.find((milestone) => totalXP < Number(milestone.threshold || 0)) || null;

  const previousThreshold = currentMilestone ? Number(currentMilestone.threshold || 0) : 0;
  const nextThreshold = nextMilestone ? Number(nextMilestone.threshold || 0) : previousThreshold;
  const span = Math.max(nextThreshold - previousThreshold, 1);
  const progressInSpan = nextMilestone ? Math.max(totalXP - previousThreshold, 0) : span;

  return {
    milestones,
    currentMilestone,
    nextMilestone,
    levelProgress: Math.min((progressInSpan / span) * 100, 100),
    remainingXP: nextMilestone ? Math.max(nextThreshold - totalXP, 0) : 0,
  };
};

function CompanySettingsPage({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'info');
  const [companyInfo, setCompanyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  const isSuperAdmin = user.role === 'super_admin';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: { street: '', city: '', postcode: '' },
    logo_path: '',
    technician_can_add_services: true,
    technician_can_add_bikes: false,
    // Booking settings
    booking_buffer_minutes: 0,
    online_booking_availability_percent: 100,
    min_booking_advance_days: 0,
    max_booking_advance_days: 30
  });

  // Achievements state
  const [achievements, setAchievements] = useState([]);
  const [achievementTemplates, setAchievementTemplates] = useState([]);
  const [achievementTotalXP, setAchievementTotalXP] = useState(0);
  const [upcomingRewards, setUpcomingRewards] = useState([]);
  const [hasAllAchievements, setHasAllAchievements] = useState(false);
  const [loadingAchievements, setLoadingAchievements] = useState(false);
  const [refreshingAchievements, setRefreshingAchievements] = useState(false);
  const subscriptionTier = companyInfo?.subscription_tier || 'basic';
  const isEnterpriseOrLifetime = ['enterprise', 'lifetime'].includes((subscriptionTier || '').toLowerCase());
  const showLockedOverlay = !isSuperAdmin && !isEnterpriseOrLifetime;
  const allowedTabs = [
    'info',
    'booking-settings',
    'reviews',
    'service-templates',
    'technicians',
    'user-connections',
    'marketing',
    'integrations',
    'achievements',
  ];

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCompanies();
    } else {
      fetchCompanyInfo();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') || 'info';
    if (allowedTabs.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  // Fetch achievements when tab changes to achievements
  useEffect(() => {
    if (activeTab === 'achievements' && companyInfo?.id) {
      fetchAchievements();
    }
  }, [activeTab, companyInfo?.id]);

  const fetchAchievements = async () => {
    if (!companyInfo?.id) return;
    try {
      setLoadingAchievements(true);
      const [tmplResp, progressResp] = await Promise.all([
        achievementAPI.getTemplates(),
        achievementAPI.getCompanyProgress(companyInfo.id),
      ]);
      setAchievementTemplates(tmplResp.data.templates || []);
      setAchievements(progressResp.data.achievements || []);
      setAchievementTotalXP(Number(progressResp.data.total_xp || 0));
      setUpcomingRewards(progressResp.data.upcoming_rewards || []);
      setHasAllAchievements(Boolean(progressResp.data.has_all_achievements));
    } catch (err) {
      console.error('Error loading achievements:', err);
    } finally {
      setLoadingAchievements(false);
    }
  };

  const handleRefreshAchievements = async () => {
    if (!companyInfo?.id) return;
    try {
      setRefreshingAchievements(true);
      const response = await achievementAPI.refreshAchievements(companyInfo.id);
      await fetchAchievements();
      if (response.data.newly_unlocked && response.data.newly_unlocked.length > 0) {
        setSuccess(`New achievement unlocked: ${response.data.newly_unlocked.join(', ')}`);
      } else {
        setSuccess('Achievements updated!');
      }
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred');
    } finally {
      setRefreshingAchievements(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companyAPI.getAllCompanies();
      const companiesList = response.data.companies || [];
      setCompanies(companiesList);
      if (companiesList.length > 0) {
        setSelectedCompanyId(companiesList[0].id);
        loadCompanyById(companiesList[0].id);
      } else {
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to load companies');
      setLoading(false);
    }
  };

  const loadCompanyById = async (companyId) => {
    try {
      setLoading(true);
      const response = await companyAPI.getCompanyDetails(companyId);
      const company = response.data.company;
      setCompanyInfo(company);
      setFormData({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || { street: '', city: '', postcode: '' },
        logo_path: company.logo_path || '',
        technician_can_add_services: company.technician_can_add_services !== false,
        technician_can_add_bikes: company.technician_can_add_bikes === true,
        // Booking settings
        booking_buffer_minutes: company.booking_buffer_minutes || 0,
        online_booking_availability_percent: company.online_booking_availability_percent || 100,
        min_booking_advance_days: company.min_booking_advance_days || 0,
        max_booking_advance_days: company.max_booking_advance_days || 30
      });
    } catch (err) {
      setError('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySelect = (companyId) => {
    setSelectedCompanyId(companyId);
    loadCompanyById(companyId);
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await companyAPI.getCompanyInfo();
      const company = response.data.company;
      setCompanyInfo(company);
      setFormData({
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || { street: '', city: '', postcode: '' },
        logo_path: company.logo_path || '',
        technician_can_add_services: company.technician_can_add_services !== false,
        technician_can_add_bikes: company.technician_can_add_bikes === true,
        // Booking settings
        booking_buffer_minutes: company.booking_buffer_minutes || 0,
        online_booking_availability_percent: company.online_booking_availability_percent || 100,
        min_booking_advance_days: company.min_booking_advance_days || 0,
        max_booking_advance_days: company.max_booking_advance_days || 30
      });
    } catch (err) {
      setError('Failed to load company information');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        technician_can_add_services: formData.technician_can_add_services,
        technician_can_add_bikes: formData.technician_can_add_bikes,
        // Booking settings
        booking_buffer_minutes: formData.booking_buffer_minutes,
        online_booking_availability_percent: formData.online_booking_availability_percent,
        min_booking_advance_days: formData.min_booking_advance_days,
        max_booking_advance_days: formData.max_booking_advance_days
      };
      if (isSuperAdmin && companyInfo) {
        updateData.company_id = companyInfo.id;
      }

      await companyAPI.updateCompanyInfo(updateData);
      setSuccess('Company information updated successfully!');
      if (!isSuperAdmin) fetchCompanyInfo();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update company information');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload PNG, JPG, GIF or WebP.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    const file = logoInputRef.current?.files?.[0];
    if (!file) {
      setError('Please select a file first');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setUploadingLogo(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('logo', file);

      const companyId = isSuperAdmin ? selectedCompanyId : companyInfo?.id;
      const response = await companyAPI.uploadLogo(formDataUpload, companyId);

      // Update with full company object (includes logo_url)
      if (response.data.company) {
        setCompanyInfo(response.data.company);
        setFormData(prev => ({ ...prev, logo_path: response.data.company.logo_path }));
      } else {
        setFormData(prev => ({ ...prev, logo_path: response.data.logo_path }));
        setCompanyInfo(prev => ({ ...prev, logo_path: response.data.logo_path }));
      }
      
      setLogoPreview(null);
      setSuccess('Logo uploaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
      
      // Clear file input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the company logo?')) return;

    setUploadingLogo(true);
    setError('');

    try {
      const companyId = isSuperAdmin ? selectedCompanyId : companyInfo?.id;
      await companyAPI.deleteLogo(companyId);

      setFormData(prev => ({ ...prev, logo_path: '' }));
      setCompanyInfo(prev => ({ ...prev, logo_path: null, logo_url: null }));
      setLogoPreview(null);
      setSuccess('Logo deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const unlockedAchievementsCount = achievements.filter((achievement) => achievement.is_unlocked).length;
  const xpTrack = getXpTrackInfo(achievementTotalXP, achievementTemplates);

  if (loading) {
    return (
      <Layout 
        user={user} 
        onLogout={onLogout} 
        pageTitle="Company Settings" 
        darkMode={darkMode} 
        onToggleDarkMode={onToggleDarkMode}
      >
        <div className="loading">Loading company information...</div>
      </Layout>
    );
  }

  return (
    <Layout 
      user={user} 
      onLogout={onLogout}
      pageTitle="Company Settings"
      pageSubtitle={companyInfo ? `Managing ${companyInfo.name}` : 'Configure your company'}
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
      subscriptionTier={subscriptionTier}
    >
      {/* Super Admin Company Selector */}
      {isSuperAdmin && companies.length > 0 && (
        <div style={{
          padding: '16px 20px',
          background: darkMode 
            ? 'linear-gradient(135deg, rgba(255, 107, 107, 0.1) 0%, rgba(255, 107, 107, 0.05) 100%)'
            : 'linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 107, 107, 0.08) 100%)',
          border: '1px solid rgba(255, 107, 107, 0.2)',
          borderRadius: '12px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <span style={{color: darkMode ? '#fca5a5' : '#dc2626', fontWeight: '600', fontSize: '14px'}}>
            <Icon name="verified" color="primary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Super Admin
          </span>
          <select 
            className="company-selector-select"
            value={selectedCompanyId || ''} 
            onChange={(e) => handleCompanySelect(parseInt(e.target.value))}
            style={{
              padding: '10px 16px',
              borderRadius: '8px',
              border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.15)',
              background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
              color: darkMode ? '#ffffff' : '#1f2937',
              minWidth: '200px',
              cursor: 'pointer'
            }}
          >
            {companies.map(c => (
              <option key={c.id} value={c.id} style={{background: darkMode ? '#1a1a3e' : '#ffffff', color: darkMode ? '#ffffff' : '#1f2937'}}>
                {c.name}
              </option>
            ))}
          </select>
          <span style={{fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'}}>
            {companies.length} companies
          </span>
        </div>
      )}

      {error && <div className="error" style={{marginBottom: '20px'}}>{error}</div>}
      {success && <div className="success" style={{marginBottom: '20px'}}>{success}</div>}

      {/* Tab navigation */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: '24px',
        padding: '4px',
        background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
        borderRadius: '14px',
        border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
      }}>
        {[
          { id: 'info', label: 'Company Info', icon: 'companies', locked: false },
          { id: 'booking-settings', label: 'Booking', icon: 'calendar', locked: showLockedOverlay },
          { id: 'reviews', label: 'Reviews', icon: 'star', locked: false },
          { id: 'service-templates', label: 'Service Templates', icon: 'service', locked: showLockedOverlay },
          { id: 'technicians', label: 'Technicians', icon: 'technician', locked: showLockedOverlay },
          { id: 'user-connections', label: 'User Connections', icon: 'profile', locked: false },
          { id: 'marketing', label: 'Marketing', icon: 'marketing', locked: showLockedOverlay },
          { id: 'integrations', label: 'Integrations', icon: 'settings', locked: showLockedOverlay },
          { id: 'achievements', label: 'Achievements', icon: 'achievements', locked: false },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => {
              setActiveTab(tab.id);
              setSearchParams({ tab: tab.id });
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              borderRadius: '10px',
              border: 'none',
              background: activeTab === tab.id
                ? (darkMode ? 'rgba(74, 140, 24, 0.25)' : 'rgba(74, 140, 24, 0.2)')
                : 'transparent',
              color: activeTab === tab.id
                ? (darkMode ? '#4A8C18' : '#3d5a20')
                : (darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'),
              fontSize: '13px',
              fontWeight: activeTab === tab.id ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            <Icon name={tab.icon} color={activeTab === tab.id ? 'primary' : 'secondary'} darkMode={darkMode} size={16} />
            {tab.label}
            {tab.locked && <Icon name="lock" size={12} color="muted" darkMode={darkMode} style={{ marginLeft: '4px', opacity: 0.7 }} />}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="card">
        {activeTab === 'info' && (
          <div>
            <h2 style={{fontSize: '18px', marginBottom: '24px', color: darkMode ? '#ffffff' : '#111827', display: 'flex', alignItems: 'center', gap: '10px'}}>
              <Icon name="companies" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '10px' }} />
              Company Information
            </h2>

            {/* Logo Upload Section */}
            <div style={{
              marginBottom: '32px',
              padding: '24px',
              background: darkMode ? 'rgba(61, 90, 32, 0.05)' : 'rgba(61, 90, 32, 0.08)',
              border: darkMode ? '1px solid rgba(61, 90, 32, 0.15)' : '1px solid rgba(61, 90, 32, 0.25)',
              borderRadius: '16px'
            }}>
              <h3 style={{
                fontSize: '15px',
                color: darkMode ? 'var(--lime-light)' : '#4a8c18',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon name="photos" color="brand" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Company Logo
              </h3>
              
              <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Current Logo Preview */}
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '16px',
                  border: darkMode ? '2px dashed rgba(255,255,255,0.2)' : '2px dashed rgba(0,0,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'
                }}>
                  {logoPreview ? (
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : companyInfo?.logo_url ? (
                    <img 
                      src={companyInfo.logo_url} 
                      alt="Company logo" 
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <img 
                      src={DEFAULT_APP_LOGO} 
                      alt="Default logo - upload to replace" 
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  )}
                </div>

                {/* Upload Controls */}
                <div style={{ flex: 1, minWidth: '200px' }}>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleLogoSelect}
                    style={{ display: 'none' }}
                    id="logo-upload"
                  />
                  
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      style={{
                        padding: '10px 20px',
                        borderRadius: '10px',
                        border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
                        background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                        color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Select Image
                    </button>
                    
                    {logoPreview && (
                      <button
                        type="button"
                        onClick={handleLogoUpload}
                        disabled={uploadingLogo}
                        className="btn btn-primary"
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload'}
                      </button>
                    )}
                    
                    {companyInfo?.logo_url && !logoPreview && (
                      <button
                        type="button"
                        onClick={handleLogoDelete}
                        disabled={uploadingLogo}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '10px',
                          border: 'none',
                          background: 'rgba(239, 68, 68, 0.15)',
                          color: '#f87171',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  
                  <p style={{
                    fontSize: '12px',
                    color: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.5)',
                    margin: 0
                  }}>
                    Supported formats: PNG, JPG, GIF, WebP. Max size: 5MB
                  </p>
                </div>
              </div>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label style={{color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151'}}>Company Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  disabled={saving}
                  placeholder="Company name"
                />
              </div>

              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
                <div className="form-group">
                  <label style={{color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151'}}>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    disabled={saving}
                    placeholder="company@example.com"
                  />
                </div>

                <div className="form-group">
                  <label style={{color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151'}}>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    disabled={saving}
                    placeholder="+36 1 234 5678"
                  />
                </div>
              </div>

              <div className="form-group">
                <label style={{color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151'}}>Street Address</label>
                <input
                  type="text"
                  value={formData.address.street || ''}
                  onChange={(e) => setFormData({...formData, address: {...formData.address, street: e.target.value}})}
                  disabled={saving}
                  placeholder="Street address"
                />
              </div>

              <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
                <div className="form-group">
                  <label style={{color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151'}}>City</label>
                  <input
                    type="text"
                    value={formData.address.city || ''}
                    onChange={(e) => setFormData({...formData, address: {...formData.address, city: e.target.value}})}
                    disabled={saving}
                    placeholder="City"
                  />
                </div>

                <div className="form-group">
                  <label style={{color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151'}}>Postcode</label>
                  <input
                    type="text"
                    value={formData.address.postcode || ''}
                    onChange={(e) => setFormData({...formData, address: {...formData.address, postcode: e.target.value}})}
                    disabled={saving}
                    placeholder="Postcode"
                  />
                </div>
              </div>

              {/* Technician Permissions - Only for Professional and Enterprise tiers */}
              {(isSuperAdmin || subscriptionTier !== 'basic') && (
              <div style={{
                marginTop: '32px',
                padding: '20px',
                background: darkMode ? 'rgba(61, 90, 32, 0.08)' : '#ffffff',
                border: darkMode ? '1px solid rgba(61, 90, 32, 0.15)' : '1px solid #e5e7eb',
                borderRadius: '12px',
                boxShadow: darkMode ? 'var(--shadow-xl)' : 'var(--shadow-md)'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  color: darkMode ? 'var(--lime-light)' : '#15803d',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Icon name="service" color="brand" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Technician Permissions
                </h3>
                <p style={{
                  fontSize: '13px',
                  color: darkMode ? 'rgba(255,255,255,0.5)' : '#4b5563',
                  marginBottom: '16px'
                }}>
                  Control what technicians can do in your company
                </p>
                <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: darkMode ? 'rgba(255,255,255,0.8)' : '#111827',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.technician_can_add_services}
                      onChange={(e) => setFormData({...formData, technician_can_add_services: e.target.checked})}
                      disabled={saving}
                    />
                    <span>Technicians can add/record services</span>
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    color: darkMode ? 'rgba(255,255,255,0.8)' : '#111827',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.technician_can_add_bikes}
                      onChange={(e) => setFormData({...formData, technician_can_add_bikes: e.target.checked})}
                      disabled={saving}
                    />
                    <span>Technicians can add new bikes</span>
                  </label>
                </div>
              </div>
              )}

              <div style={{display: 'flex', gap: '12px', marginTop: '24px'}}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>

            {/* Working Hours Section */}
            <div style={{marginTop: '48px', paddingTop: '32px', borderTop: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb'}}>
              <WorkingHoursManager companyId={isSuperAdmin ? selectedCompanyId : companyInfo?.id} />
            </div>

            {companyInfo && (
              <div style={{marginTop: '32px', padding: '20px', background: 'var(--bg-overlay-light)', borderRadius: '12px'}}>
                <h3 style={{fontSize: '14px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                  Current Information
                </h3>
                <div style={{display: 'grid', gap: '8px', fontSize: '14px'}}>
                  <div style={{display: 'flex', gap: '8px'}}>
                    <span style={{color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'}}>ID:</span>
                    <span style={{color: darkMode ? '#ffffff' : '#111827'}}>{companyInfo.display_id || companyInfo.id}</span>
                  </div>
                  <div style={{display: 'flex', gap: '8px'}}>
                    <span style={{color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'}}>Status:</span>
                    <span style={{color: companyInfo.is_active ? '#1A2811' : '#ef4444'}}>
                      {companyInfo.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Booking Settings Tab */}
        {activeTab === 'booking-settings' && (
          showLockedOverlay ? (
            <EnterpriseLockedOverlay
              title={ENTERPRISE_LOCKED_FEATURES['booking-settings'].title}
              description={ENTERPRISE_LOCKED_FEATURES['booking-settings'].description}
              darkMode={darkMode}
            />
          ) : (
          <div>
            <h2 style={{fontSize: '18px', marginBottom: '24px', color: darkMode ? '#ffffff' : '#111827', display: 'flex', alignItems: 'center', gap: '10px'}}>
              <Icon name="calendar" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '10px' }} />
              Online Booking Settings
            </h2>

            <div style={{
              marginBottom: '24px',
              padding: '16px',
              background: darkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.08)',
              border: '1px solid rgba(59, 130, 246, 0.25)',
              borderRadius: '12px',
              fontSize: '14px',
              color: darkMode ? '#93c5fd' : '#1e40af'
            }}>
              <Icon name="info" color="info" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
              These settings control how customers see your online booking availability.
            </div>

            <form onSubmit={handleSubmit}>
              {/* Buffer Time */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                borderRadius: '12px'
              }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', color: darkMode ? '#ffffff' : '#111827' }}>
                  <Icon name="schedule" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Buffer time
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                  Extra time after each booking (like Square). Useful for cleanup and prep.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="number"
                    min="0"
                    max="120"
                    step="5"
                    value={formData.booking_buffer_minutes}
                    onChange={(e) => setFormData({...formData, booking_buffer_minutes: parseInt(e.target.value) || 0})}
                    style={{
                      width: '100px',
                      padding: '10px',
                      borderRadius: '8px',
                      border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #d1d5db',
                      background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                      color: darkMode ? '#ffffff' : '#111827'
                    }}
                  />
                  <span style={{ color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>minutes</span>
                </div>
              </div>

              {/* Availability Percentage */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                borderRadius: '12px'
              }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', color: darkMode ? '#ffffff' : '#111827' }}>
                  <Icon name="analytics" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Online availability percentage
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                  What percent of time slots should be shown online? 
                  <br/>Keep the remainder for phone bookings or walk-ins.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <input
                    type="range"
                    className="booking-availability-slider"
                    min="0"
                    max="100"
                    step="5"
                    value={formData.online_booking_availability_percent}
                    onChange={(e) => setFormData({...formData, online_booking_availability_percent: parseInt(e.target.value) || 0})}
                    style={{ flex: 1, maxWidth: '300px' }}
                  />
                  <input
                    type="number"
                    className="booking-availability-number"
                    min="0"
                    max="100"
                    value={formData.online_booking_availability_percent}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (!isNaN(v)) setFormData({...formData, online_booking_availability_percent: Math.max(0, Math.min(100, v))});
                    }}
                  />
                  <span style={{ 
                    padding: '6px 4px',
                    fontWeight: '600',
                    color: '#3d5a20',
                    fontSize: '14px'
                  }}>%</span>
                </div>
              </div>

              {/* Minimum Advance Days */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                borderRadius: '12px'
              }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', color: darkMode ? '#ffffff' : '#111827' }}>
                  <Icon name="calendarMonth" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Minimum advance booking (days)
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                  How many days ahead must customers book? (0 allows same-day bookings)
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={formData.min_booking_advance_days}
                    onChange={(e) => setFormData({...formData, min_booking_advance_days: parseInt(e.target.value) || 0})}
                    style={{
                      width: '100px',
                      padding: '10px',
                      borderRadius: '8px',
                      border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #d1d5db',
                      background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                      color: darkMode ? '#ffffff' : '#111827'
                    }}
                  />
                  <span style={{ color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>days</span>
                </div>
              </div>

              {/* Maximum Advance Days */}
              <div style={{
                marginBottom: '24px',
                padding: '20px',
                background: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                borderRadius: '12px'
              }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '15px', color: darkMode ? '#ffffff' : '#111827' }}>
                  <Icon name="calendar" color="primary" darkMode={darkMode} size={15} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                  Maximum advance booking (days)
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                  How many days ahead can customers book at most?
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.max_booking_advance_days}
                    onChange={(e) => setFormData({...formData, max_booking_advance_days: parseInt(e.target.value) || 30})}
                    style={{
                      width: '100px',
                      padding: '10px',
                      borderRadius: '8px',
                      border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #d1d5db',
                      background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                      color: darkMode ? '#ffffff' : '#111827'
                    }}
                  />
                  <span style={{ color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>days</span>
                </div>
              </div>

              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save settings'}
              </button>
            </form>
          </div>
          )
        )}
        {activeTab === 'reviews' && <CompanyReviewsManager user={user} />}
        {activeTab === 'service-templates' && (
          showLockedOverlay ? (
            <EnterpriseLockedOverlay
              title={ENTERPRISE_LOCKED_FEATURES['service-templates'].title}
              description={ENTERPRISE_LOCKED_FEATURES['service-templates'].description}
              darkMode={darkMode}
            />
          ) : (
            <ServiceTemplatesManager companyId={isSuperAdmin ? selectedCompanyId : companyInfo?.id} />
          )
        )}
        {activeTab === 'technicians' && (
          showLockedOverlay ? (
            <EnterpriseLockedOverlay
              title={ENTERPRISE_LOCKED_FEATURES['technicians'].title}
              description={ENTERPRISE_LOCKED_FEATURES['technicians'].description}
              darkMode={darkMode}
            />
          ) : (
            <TechniciansManager companyId={isSuperAdmin ? selectedCompanyId : companyInfo?.id} darkMode={darkMode} />
          )
        )}
        {activeTab === 'user-connections' && <UserConnectionsManager companyId={isSuperAdmin ? selectedCompanyId : companyInfo?.id} darkMode={darkMode} />}
        {activeTab === 'marketing' && (
          showLockedOverlay ? (
            <EnterpriseLockedOverlay
              title={ENTERPRISE_LOCKED_FEATURES['marketing'].title}
              description={ENTERPRISE_LOCKED_FEATURES['marketing'].description}
              darkMode={darkMode}
            />
          ) : (
            <MarketingCampaignManager companyId={isSuperAdmin ? selectedCompanyId : companyInfo?.id} darkMode={darkMode} />
          )
        )}
        {activeTab === 'integrations' && (
          showLockedOverlay ? (
            <EnterpriseLockedOverlay
              title={ENTERPRISE_LOCKED_FEATURES['integrations'].title}
              description={ENTERPRISE_LOCKED_FEATURES['integrations'].description}
              darkMode={darkMode}
            />
          ) : (
            <SquareIntegrationSettings companyInfo={companyInfo} darkMode={darkMode} />
          )
        )}
        
        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 style={{fontSize: '18px', margin: 0, color: darkMode ? '#ffffff' : '#111827', display: 'flex', alignItems: 'center', gap: '10px'}}>
                <Icon name="achievement" color="primary" darkMode={darkMode} size={18} style={{ verticalAlign: 'middle', marginRight: '10px' }} />
                Achievements
              </h2>
              <button
                onClick={handleRefreshAchievements}
                disabled={refreshingAchievements}
                className="btn btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Icon name="loading" color="secondary" darkMode={darkMode} size={14} style={{ 
                  display: 'inline-block',
                  animation: refreshingAchievements ? 'spin 1s linear infinite' : 'none'
                }} />
                {refreshingAchievements ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {loadingAchievements ? (
              <div className="loading">Loading achievements...</div>
            ) : (
              <>
                {/* Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    padding: '20px',
                    background: 'rgba(61, 90, 32, 0.1)',
                    border: '1px solid rgba(61, 90, 32, 0.3)',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#3d5a20' }}>
                      {unlockedAchievementsCount}
                    </div>
                    <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#4b5563' }}>Unlocked</div>
                  </div>
                  <div style={{
                    padding: '20px',
                    background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
                    border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e5e7eb',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: darkMode ? '#ffffff' : '#111827' }}>
                      {achievementTemplates.length}
                    </div>
                    <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#4b5563' }}>Total</div>
                  </div>
                  <div style={{
                    padding: '20px',
                    background: darkMode ? 'rgba(255, 215, 0, 0.08)' : 'rgba(255, 215, 0, 0.12)',
                    border: darkMode ? '1px solid rgba(255, 215, 0, 0.25)' : '1px solid rgba(255, 215, 0, 0.35)',
                    borderRadius: '12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '32px', fontWeight: '700', color: '#ffd700' }}>
                      {achievementTotalXP}
                    </div>
                    <div style={{ fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#4b5563' }}>Total XP</div>
                  </div>
                </div>

                {xpTrack.milestones.length > 0 && (
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    borderRadius: '12px',
                    border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                    background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      <strong style={{ color: darkMode ? '#4A8C18' : '#3d5a20', fontSize: '14px' }}>XP Progress</strong>
                      {xpTrack.nextMilestone ? (
                        <span style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#4b5563' }}>
                          {xpTrack.remainingXP} XP to next reward checkpoint
                        </span>
                      ) : (
                        <span style={{ fontSize: '12px', color: '#4A8C18' }}>Top XP checkpoint reached</span>
                      )}
                    </div>
                    <div style={{ height: '10px', borderRadius: '999px', background: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: '10px' }}>
                      <div style={{
                        width: `${xpTrack.levelProgress}%`,
                        height: '100%',
                        background: 'linear-gradient(90deg, #3d5a20, #84cc16)'
                      }} />
                    </div>
                    <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '2px' }}>
                      {xpTrack.milestones.map((milestone, idx) => {
                        const threshold = Number(milestone.threshold || 0);
                        const reached = achievementTotalXP >= threshold;
                        return (
                          <div
                            key={milestone.id || milestone.code || idx}
                            style={{
                              minWidth: '94px',
                              padding: '8px',
                              borderRadius: '8px',
                              background: reached ? 'rgba(74, 140, 24, 0.2)' : (darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
                              border: reached ? '1px solid rgba(74, 140, 24, 0.35)' : (darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)'),
                              flexShrink: 0
                            }}
                          >
                            <div style={{ fontSize: '11px', fontWeight: '700', color: reached ? '#4A8C18' : (darkMode ? 'rgba(255,255,255,0.7)' : '#374151') }}>
                              {milestone.name || `Milestone ${idx + 1}`}
                            </div>
                            <div style={{ fontSize: '10px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>{threshold} XP</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(hasAllAchievements || upcomingRewards.length > 0) && (
                  <div style={{
                    marginBottom: '24px',
                    padding: '16px',
                    borderRadius: '12px',
                    border: '1px solid rgba(245, 158, 11, 0.25)',
                    background: 'rgba(245, 158, 11, 0.08)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#f59e0b' }}>Reward roadmap</h4>
                    {hasAllAchievements && (
                      <div style={{ marginBottom: '8px', fontSize: '12px', color: '#4A8C18', fontWeight: '600' }}>
                        All achievements completed: 10% discount active.
                      </div>
                    )}
                    {upcomingRewards.slice(0, 4).map((reward, idx) => (
                      <div key={idx} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        flexWrap: 'wrap',
                        padding: '8px 0',
                        borderTop: idx === 0 ? 'none' : '1px solid rgba(245, 158, 11, 0.2)'
                      }}>
                        <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.8)' : '#111827', fontWeight: '600' }}>
                          {reward.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#f59e0b' }}>
                          {reward.reward} ({reward.progress}/{reward.threshold})
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Achievement Cards Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: '16px'
                }}>
                  {achievementTemplates.map(template => {
                    const achievement = achievements.find(a => a.achievement_template_id === template.id);
                    const isUnlocked = achievement?.is_unlocked;
                    const progress = achievement?.progress || 0;
                    const tier = template.tier || 'bronze';
                    const tierStyle = TIER_COLORS[tier] || TIER_COLORS.bronze;

                    return (
                      <div
                        key={template.id}
                        style={{
                          padding: '20px',
                          background: isUnlocked ? tierStyle.bg : 'rgba(255,255,255,0.03)',
                          border: `2px solid ${isUnlocked ? tierStyle.border : 'rgba(255,255,255,0.1)'}`,
                          borderRadius: '16px',
                          opacity: isUnlocked ? 1 : 0.7,
                          transition: 'all 0.3s ease',
                          position: 'relative'
                        }}
                      >
                        {isUnlocked && (
                          <div style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '12px',
                            background: tierStyle.border,
                            color: '#0a0a1a',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            <Icon name="success" color="primary" darkMode={darkMode} size={11} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                            Unlocked
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon name="achievement" color="warning" darkMode={darkMode} size={32} />
                          </span>
                          <div>
                            <h3 style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              margin: 0,
                              color: isUnlocked ? tierStyle.text : 'rgba(255,255,255,0.7)'
                            }}>
                              {template.name}
                            </h3>
                            <span style={{
                              fontSize: '11px',
                              textTransform: 'uppercase',
                              color: tierStyle.text,
                              fontWeight: '600'
                            }}>
                              {tier}
                            </span>
                          </div>
                        </div>
                        <p style={{
                          fontSize: '13px',
                          color: 'rgba(255,255,255,0.6)',
                          margin: '0 0 12px 0',
                          lineHeight: '1.5'
                        }}>
                          {template.description}
                        </p>
                        {!isUnlocked && template.threshold && (
                          <div style={{
                            background: 'rgba(255,255,255,0.05)',
                            borderRadius: '8px',
                            height: '8px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${Math.min((progress / template.threshold) * 100, 100)}%`,
                              height: '100%',
                              background: tierStyle.border,
                              transition: 'width 0.3s ease'
                            }} />
                          </div>
                        )}
                        {!isUnlocked && template.threshold && (
                          <div style={{
                            fontSize: '12px',
                            color: 'rgba(255,255,255,0.5)',
                            marginTop: '8px',
                            textAlign: 'right'
                          }}>
                            {progress} / {template.threshold}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {achievementTemplates.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: 'rgba(255,255,255,0.5)'
                  }}>
                    <div style={{ marginBottom: '16px' }}>
                      <Icon name="achievement" color="warning" darkMode={darkMode} size={48} />
                    </div>
                    <p>No achievements available</p>
                  </div>
                )}

                {/* Tier Legend */}
                <div style={{
                  marginTop: '32px',
                  padding: '16px',
                  background: 'rgba(255,255,255,0.02)',
                  borderRadius: '12px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'rgba(255,255,255,0.5)' }}>Achievement tiers</h4>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    {Object.entries(TIER_COLORS).map(([tier, colors]) => (
                      <div key={tier} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          background: colors.border
                        }} />
                        <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}>
                          {tier}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

export default CompanySettingsPage;
